//@ts-nocheck
import "assets/css/email-popup.css";
import React, { useEffect, useState } from "react";
import { Button, Dialog, DialogTitle, DialogContent, Grid, Box, Alert } from "@mui/material";
import MeetingForm from "../Meetings/MeetingForm";
import {
  cancelMeeting,
  createMeeting,
  downloadRecording,
  fetchMeetingsByApplicationId,
  updateMeeting,
} from "../Meetings/MeetingApi";
import { post } from "requests";
import { useSubmissionStore } from "../../../../models/applications/SubmissionStore";
import { useStoreState } from "easy-peasy";
import InnerTable from "lib/Tables/InnerTable";
import { convertTo12HourFormat } from "../../../../utils/CalanderUtils";
import MDButton from "components/MDButton";

const MeetingPage = ({ applicationId, isReadOnly }) => {
  const [meetings, setMeetings] = useState([]);
  const [currentMeeting, setCurrentMeeting] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [recipientsByCategory, setRecipientsByCategory] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState(null);

  const { merchantBusinessInfo, dealApplicationData } = useSubmissionStore();

  const offerDetails = useStoreState((state) => state.offerStore.contractTerms);

  useEffect(() => {
    loadMeetings();
    fetchRecipientsByCategory();
  }, []);

  const showAlert = (message, type = "success") => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => {
      setAlertMessage("");
      setAlertType(null);
    }, 4000);
  };

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setCurrentMeeting(null);
    setShowModal(false);
  };

  const loadMeetings = async () => {
    try {
      const data = await fetchMeetingsByApplicationId(applicationId);
      setMeetings(data);
    } catch (error) {
      console.error("Failed to fetch meetings:", error);
      showAlert("Failed to fetch meetings.", "error");
    }
  };

  const fetchRecipientsByCategory = () => {
    post("fetchRecipientsByCategory", { applicationId })
      .then(setRecipientsByCategory)
      .catch(() => showAlert("Failed to fetch recipients", "error"));
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (currentMeeting) {
        formData.finalEmailRecipients = currentMeeting.finalEmailRecipients;
        await updateMeeting(currentMeeting.zoomMeetingId, formData);
        showAlert("Meeting updated successfully", "success");
      } else {
        formData.finalEmailRecipients = formData.finalEmailRecipients.map((r) => r.email);
        const meetingDetails = { ...formData, applicationId };
        await createMeeting(meetingDetails);
        showAlert("Meeting created successfully", "success");
      }
      handleCloseModal();
      loadMeetings();
    } catch (error) {
      console.error("Failed to submit form", error);
      showAlert("Failed to save meeting", "error");
    }
  };

  const onCancel = async (meeting) => {
    try {
      await cancelMeeting(meeting.id, meeting);
      showAlert("Meeting canceled successfully", "success");
      loadMeetings();
    } catch (error) {
      console.error("Failed to cancel the meeting", error);
      showAlert("Failed to cancel the meeting", "error");
    }
  };

  const onReschedule = (meeting) => {
    setCurrentMeeting(meeting);
    setShowModal(true);
  };

  const onDownload = async (meeting) => {
    try {
      const response = await downloadRecording(meeting.zoomMeetingId);
      const blob = new Blob([response], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `meeting_${meeting.zoomMeetingId}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading recording", error);
      if (error.response?.status === 404) {
        showAlert(`Recording for meeting ID ${meeting.zoomMeetingId} not found`, "error");
      } else {
        showAlert(`Error downloading recording for meeting ID ${meeting.zoomMeetingId}`, "error");
      }
    }
  };

  const columns = [
    { Header: "Meeting Description", width: "300px", accessor: "description" },
    {
      Header: "Start Date",
      width: "70px",
      accessor: "startDate",
    },
    {
      Header: "Start Time",
      accessor: "startTime",
      width: "70px",
      Cell: ({ value }) => convertTo12HourFormat(value),
    },
    {
      Header: "Duration",
      accessor: "duration",
      width: "70px",
      Cell: ({ value }) => `${value} minutes`,
    },
    {
      Header: "Meeting ID / Password",
      accessor: "zoomMeetingId",
      Cell: ({ row }) => (
        <div>
          <div>ID: {row.original.zoomMeetingId}</div>
          <div>Password: {row.original.password}</div>
        </div>
      ),
    },
    {
      Header: "Participants",
      accessor: "recipientList",
      width: 150,
      Cell: ({ value }) => (
        <div style={{ whiteSpace: "normal" }}>
          {Array.isArray(value) && value.length > 0 ? (
            value.map((v, i) => <div key={i}>{v}</div>)
          ) : (
            <em></em>
          )}
        </div>
      ),
    },
    { Header: "Status", width: "70px", accessor: "status" },
    {
      Header: "Actions",
      accessor: "actions",
      width: 140,
      Cell: ({ row }) => {
        const meeting = row.original;
        return (
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {meeting.status !== "Completed" && meeting.status !== "Cancelled" && (
              <>
                <MDButton
                  size="small"
                  color="success"
                  variant="contained"
                  onClick={() => window.open(meeting.joinUrl, "_blank")}
                  disabled={!meeting.joinUrl}
                >
                  Join
                </MDButton>
                <MDButton
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={() => onReschedule(meeting)}
                >
                  Edit
                </MDButton>
                <MDButton
                  size="small"
                  color="error"
                  variant="contained"
                  onClick={() => onCancel(meeting)}
                >
                  Cancel
                </MDButton>
              </>
            )}
            {meeting.status === "Completed" && (
              <MDButton color={"secondary"} variant="outlined" onClick={() => onDownload(meeting)}>
                Download
              </MDButton>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div style={isReadOnly ? { pointerEvents: "none", opacity: 0.7 } : {}}>
      <Box sx={{ p: 0.5 }}>
        <Grid container justifyContent="flex-end" sx={{ mb: 0.5 }}>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#2f929f", color: "#fff" }}
            onClick={handleShowModal}
          >
            Schedule New Meeting
          </Button>
        </Grid>

        {alertMessage && (
          <Alert severity={alertType} sx={{ mb: 2 }}>
            {alertMessage}
          </Alert>
        )}

        <Dialog open={showModal} onClose={handleCloseModal} fullWidth maxWidth="md">
          <DialogTitle>{currentMeeting ? "Edit Meeting" : "Schedule New Meeting"}</DialogTitle>
          <DialogContent>
            <MeetingForm
              onSubmit={handleFormSubmit}
              meeting={currentMeeting}
              recipientsByCategory={recipientsByCategory}
              closeModal={handleCloseModal}
              applicationId={applicationId}
              offerDetailsFormStore={offerDetails}
              merchantStore={merchantBusinessInfo}
              dealApplicationStore={dealApplicationData}
            />
          </DialogContent>
        </Dialog>

        {meetings && meetings.length > 0 && (
          <InnerTable
            table={{ columns, rows: meetings }}
            paginationEnabled={false}
            selectedItem={[]}
            onSelectItem={() => {}}
            getRowId={(row) => row.zoomMeetingId}
            selectAllRows={() => {}}
          />
        )}
      </Box>
    </div>
  );
};

export default MeetingPage;
